import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useMotionValueEvent, useTransform } from 'framer-motion';
import { useReduceMotion } from '@/lib/useReduceMotion';

/**
 * Elastic slider — adapted from ReactBits (Components/ElasticSlider). The drag
 * physics (overflow stretch + spring snap, side-icon nudge) are kept verbatim;
 * adaptations for this project:
 *   - imports from 'framer-motion' (not 'motion/react', which isn't installed)
 *   - controlled: `value` + `onChange` so it can drive a real volume store
 *   - brand styling (accent fill on a subtle track) + an optional value label
 */

const MAX_OVERFLOW = 50;

interface ElasticSliderProps {
  /** Controlled current value. */
  value?: number;
  startingValue?: number;
  maxValue?: number;
  className?: string;
  isStepped?: boolean;
  stepSize?: number;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onChange?: (value: number) => void;
  /** Format the trailing label (e.g. v => `${v}%`). Omit to hide the label. */
  formatValue?: (value: number) => string;
}

export function ElasticSlider({
  value = 50,
  startingValue = 0,
  maxValue = 100,
  className = '',
  isStepped = false,
  stepSize = 1,
  leftIcon = <>-</>,
  rightIcon = <>+</>,
  onChange,
  formatValue,
}: ElasticSliderProps) {
  const reduce = useReduceMotion();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [internal, setInternal] = useState<number>(value);
  const [region, setRegion] = useState<'left' | 'middle' | 'right'>('middle');
  const clientX = useMotionValue(0);
  const overflow = useMotionValue(0);
  const scale = useMotionValue(1);

  // Controlled: reflect external changes (store updates from elsewhere).
  useEffect(() => {
    setInternal(value);
  }, [value]);

  useMotionValueEvent(clientX, 'change', (latest: number) => {
    if (reduce) {
      overflow.jump(0);
      setRegion('middle');
      return;
    }
    if (sliderRef.current) {
      const { left, right } = sliderRef.current.getBoundingClientRect();
      let newValue: number;
      if (latest < left) {
        setRegion('left');
        newValue = left - latest;
      } else if (latest > right) {
        setRegion('right');
        newValue = latest - right;
      } else {
        setRegion('middle');
        newValue = 0;
      }
      overflow.jump(decay(newValue, MAX_OVERFLOW));
    }
  });

  const commit = (next: number) => {
    setInternal(next);
    onChange?.(next);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons > 0 && sliderRef.current) {
      const { left, width } = sliderRef.current.getBoundingClientRect();
      let next = startingValue + ((e.clientX - left) / width) * (maxValue - startingValue);
      if (isStepped) next = Math.round(next / stepSize) * stepSize;
      next = Math.min(Math.max(next, startingValue), maxValue);
      commit(next);
      clientX.jump(e.clientX);
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    handlePointerMove(e);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = () => {
    if (reduce) overflow.jump(0);
    else animate(overflow, 0, { type: 'spring', bounce: 0.5 });
  };

  const rangePct = () => {
    const total = maxValue - startingValue;
    return total === 0 ? 0 : ((internal - startingValue) / total) * 100;
  };

  return (
    <motion.div
      onHoverStart={() => !reduce && animate(scale, 1.2)}
      onHoverEnd={() => !reduce && animate(scale, 1)}
      onTouchStart={() => !reduce && animate(scale, 1.2)}
      onTouchEnd={() => !reduce && animate(scale, 1)}
      style={{ scale, opacity: useTransform(scale, [1, 1.2], [0.75, 1]) }}
      className={`flex touch-none select-none items-center gap-2.5 ${className}`}
    >
      <motion.div
        className="shrink-0 text-ink-muted"
        animate={{ scale: region === 'left' ? [1, 1.4, 1] : 1, transition: { duration: 0.25 } }}
        style={{ x: useTransform(() => (region === 'left' ? -overflow.get() / scale.get() : 0)) }}
      >
        {leftIcon}
      </motion.div>

      <div
        ref={sliderRef}
        className="relative flex flex-grow cursor-grab touch-none select-none items-center py-3"
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handlePointerUp}
      >
        <motion.div
          style={{
            scaleX: useTransform(() => {
              if (sliderRef.current) {
                const { width } = sliderRef.current.getBoundingClientRect();
                return 1 + overflow.get() / width;
              }
              return 1;
            }),
            scaleY: useTransform(overflow, [0, MAX_OVERFLOW], [1, 0.8]),
            transformOrigin: useTransform(() => {
              if (sliderRef.current) {
                const { left, width } = sliderRef.current.getBoundingClientRect();
                return clientX.get() < left + width / 2 ? 'right' : 'left';
              }
              return 'center';
            }),
            height: useTransform(scale, [1, 1.2], [6, 12]),
            marginTop: useTransform(scale, [1, 1.2], [0, -3]),
            marginBottom: useTransform(scale, [1, 1.2], [0, -3]),
          }}
          className="flex flex-grow"
        >
          <div
            className="relative h-full flex-grow overflow-hidden rounded-full"
            style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
          >
            <div
              className="absolute h-full rounded-full bg-accent"
              style={{ width: `${rangePct()}%` }}
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        className="shrink-0 text-ink-muted"
        animate={{ scale: region === 'right' ? [1, 1.4, 1] : 1, transition: { duration: 0.25 } }}
        style={{ x: useTransform(() => (region === 'right' ? overflow.get() / scale.get() : 0)) }}
      >
        {rightIcon}
      </motion.div>

      {formatValue && (
        <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-ink-muted">
          {formatValue(Math.round(internal))}
        </span>
      )}
    </motion.div>
  );
}

function decay(value: number, max: number): number {
  if (max === 0) return 0;
  const entry = value / max;
  const sigmoid = 2 * (1 / (1 + Math.exp(-entry)) - 0.5);
  return sigmoid * max;
}

export default ElasticSlider;
